import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, ChevronDown, Lightbulb, Plus, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import {
  chavesDeConhecimento,
  conhecimentoDoChamado,
  sugerirParaChamado,
  vincularAoChamado,
} from '@/features/knowledge/conhecimento.api';
import {
  CorpoDoArtigo,
  EtiquetaDeRevisao,
  EtiquetaDeSituacao,
  EtiquetaDeVisibilidade,
} from '@/features/knowledge/componentes';
import { CAMINHO_ARTIGO_NOVO, LinkDoArtigo, useIrPara } from '@/features/knowledge/navegacao';
import { obterArtigo } from '@/features/knowledge/conhecimento.api';
import type { SugestaoDeArtigo } from '@/features/knowledge/tipos';

/**
 * ★ O conhecimento dentro do chamado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  É este painel que paga a Fase 8
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Uma base de conhecimento que só existe quando alguém lembra de abrir a
 * biblioteca é um custo: escreve-se, arquiva-se, ninguém lê. O que muda a
 * operação é o operador abrir um chamado e a Central dizer, **antes do
 * diagnóstico**, "já escrevemos sobre isto, e o procedimento é este".
 *
 * Por isso o painel:
 *
 *   · **carrega sozinho** ao abrir o chamado — ninguém digita nada;
 *   · **diz por que** cada artigo apareceu ("mesmo serviço", "casou 4 termos").
 *     Sugestão sem justificativa é adivinhação, e o operador aprende a ignorar;
 *   · **registra o uso com um clique**, e é esse registro que alimenta
 *     `resolvedCount` — o número que depois decide manter ou aposentar o
 *     artigo, e que mede a deflexão;
 *   · **oferece escrever** quando não acha nada. O melhor momento para escrever
 *     um artigo é o chamado difícil que acabou de ser resolvido: a solução está
 *     fresca e escrevê-la custa menos.
 */
export function SugestoesDeArtigo({ ticketId }: { ticketId: string }) {
  const cliente = useQueryClient();
  const navegar = useIrPara();
  const [falha, setFalha] = useState<Falha | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  const sugestoes = useQuery({
    queryKey: chavesDeConhecimento.sugestoes(ticketId),
    queryFn: () => sugerirParaChamado(ticketId),
  });
  const usados = useQuery({
    queryKey: chavesDeConhecimento.doChamado(ticketId),
    queryFn: () => conhecimentoDoChamado(ticketId),
  });

  const registrar = useMutation({
    mutationFn: (entrada: { articleId: string; resolved?: boolean; helpful?: boolean }) =>
      vincularAoChamado(entrada.articleId, {
        ticketId,
        ...(entrada.resolved !== undefined ? { resolved: entrada.resolved } : {}),
        ...(entrada.helpful !== undefined ? { helpful: entrada.helpful } : {}),
      }),
    onSuccess: () => {
      setFalha(null);
      void cliente.invalidateQueries({ queryKey: chavesDeConhecimento.sugestoes(ticketId) });
      void cliente.invalidateQueries({ queryKey: chavesDeConhecimento.doChamado(ticketId) });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const lista = sugestoes.data ?? [];
  const jaUsados = usados.data?.usados ?? [];
  const gerados = usados.data?.gerados ?? [];

  return (
    <section className="space-y-3" aria-labelledby="titulo-conhecimento">
      <div className="flex items-center justify-between gap-2">
        <h2
          id="titulo-conhecimento"
          className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
        >
          <Lightbulb className="h-4 w-4 text-primary" aria-hidden />
          Conhecimento
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navegar({ to: CAMINHO_ARTIGO_NOVO, search: { chamado: ticketId } })}
          title="Cria o artigo já vinculado a este chamado"
        >
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          Escrever artigo
        </Button>
      </div>

      {falha && <AvisoDeFalha falha={falha} />}

      {sugestoes.isPending ? (
        <SkeletonRegion label="Procurando artigos">
          <Skeleton className="h-20 w-full rounded-lg" />
        </SkeletonRegion>
      ) : lista.length === 0 && jaUsados.length === 0 && gerados.length === 0 ? (
        <Card>
          <CardBody className="space-y-2 py-4 text-center">
            <BookOpen className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">
              A base ainda não tem nada sobre isto.
            </p>
            <p className="text-xs text-muted-foreground">
              Se este chamado der trabalho, vale escrever o artigo ao fechá-lo — enquanto a
              solução está fresca.
            </p>
          </CardBody>
        </Card>
      ) : (
        <ul className="space-y-2">
          {lista.map((s) => (
            <li key={s.id}>
              <LinhaDeSugestao
                sugestao={s}
                expandido={aberto === s.id}
                aoExpandir={() => setAberto(aberto === s.id ? null : s.id)}
                aoUsar={(resolved) => registrar.mutate({ articleId: s.id, resolved })}
                aoAvaliar={(helpful) => registrar.mutate({ articleId: s.id, helpful })}
                registrando={registrar.isPending}
              />
            </li>
          ))}
        </ul>
      )}

      {gerados.length > 0 && (
        <div className="space-y-1.5 pt-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            Artigos que nasceram deste chamado
          </h3>
          <ul className="space-y-1">
            {gerados.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center gap-2 text-sm">
                <LinkDoArtigo artigoId={g.id} className="text-primary hover:underline">
                  {g.code}
                </LinkDoArtigo>
                <span className="truncate text-muted-foreground">{g.title}</span>
                <EtiquetaDeSituacao situacao={g.status} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Uma sugestão.
 *
 * Fechada mostra título, resumo e **os motivos**. Aberta busca o corpo — e só
 * aí: carregar o texto inteiro de cinco artigos para mostrar cinco resumos
 * gastaria banco e rede em quatro deles que ninguém vai ler.
 */
function LinhaDeSugestao({
  sugestao,
  expandido,
  aoExpandir,
  aoUsar,
  aoAvaliar,
  registrando,
}: {
  sugestao: SugestaoDeArtigo;
  expandido: boolean;
  aoExpandir: () => void;
  aoUsar: (resolveu: boolean) => void;
  aoAvaliar: (util: boolean) => void;
  registrando: boolean;
}) {
  const corpo = useQuery({
    queryKey: chavesDeConhecimento.artigo(sugestao.id),
    queryFn: () => obterArtigo(sugestao.id),
    enabled: expandido,
  });

  return (
    <Card className={cn(sugestao.jaVinculado && 'ring-1 ring-inset ring-primary/25')}>
      <CardBody className="space-y-2 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <LinkDoArtigo
            artigoId={sugestao.id}
            className="font-mono text-xs text-muted-foreground hover:text-primary"
          >
            {sugestao.code}
          </LinkDoArtigo>
          <EtiquetaDeVisibilidade visibilidade={sugestao.visibility} />
          <EtiquetaDeRevisao nextReviewAt={sugestao.nextReviewAt} />
          {sugestao.resolvedCount > 0 && (
            <span
              className="text-xs text-success"
              title="Chamados que este artigo já resolveu. O contador é mantido pelo banco."
            >
              resolveu {sugestao.resolvedCount}
            </span>
          )}
          {sugestao.jaVinculado && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
              <Check className="h-3 w-3" aria-hidden />
              já registrado neste chamado
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={aoExpandir}
          className="flex w-full items-start gap-1.5 text-left"
          aria-expanded={expandido}
        >
          <ChevronDown
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              expandido && 'rotate-180',
            )}
            aria-hidden
          />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">{sugestao.title}</span>
            <span className="block text-sm text-muted-foreground">{sugestao.summary}</span>
          </span>
        </button>

        {/*
          Por que este artigo apareceu. Sugestão sem justificativa é adivinhação:
          o operador não sabe se confia, e depois de duas surpresas passa a
          ignorar o painel inteiro.
        */}
        {sugestao.motivos.length > 0 && (
          <ul className="flex flex-wrap gap-1.5 pl-5">
            {sugestao.motivos.map((m) => (
              <li
                key={m}
                className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground ring-1 ring-inset ring-border"
              >
                {m}
              </li>
            ))}
          </ul>
        )}

        {expandido && (
          <div className="pl-5 pt-1">
            {corpo.isPending ? (
              <Skeleton className="h-24 w-full rounded" />
            ) : corpo.isError ? (
              <AvisoDeFalha falha={classificarFalha(corpo.error)} />
            ) : (
              <CorpoDoArtigo html={corpo.data.bodyHtml} />
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pl-5 pt-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={registrando}
            onClick={() => aoUsar(true)}
            title="Registra que este artigo resolveu o chamado. É o número que mede a deflexão."
          >
            Resolveu o chamado
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={registrando}
            onClick={() => aoUsar(false)}
            title="Registra a consulta sem afirmar que resolveu."
          >
            Consultei
          </Button>
          <span className="ml-auto inline-flex items-center gap-1">
            <button
              type="button"
              disabled={registrando}
              onClick={() => aoAvaliar(true)}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-success"
              aria-label="Marcar como útil"
              title="Útil"
            >
              <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              disabled={registrando}
              onClick={() => aoAvaliar(false)}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
              aria-label="Marcar como não útil"
              title="Não ajudou — é o sinal de que a base está devolvendo ruído"
            >
              <ThumbsDown className="h-3.5 w-3.5" aria-hidden />
            </button>
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
